// src/components/PlaybookViewer/PlaybookViewer.tsx
// =================================================================
// PLAYBOOK VIEWER - MINIMAL INLINE REFERENCE
// =================================================================
//
// A collapsible "show your work" disclosure that appears inline within
// AI chat responses when the AI consults the client's playbook.
// Designed to feel like supplementary reference material, not primary content.
//
// Design Philosophy: The reason is PRIMARY info when collapsed.
// "What did the AI look at?" without cluttering the conversation.
// =================================================================

import * as React from "react";
import { useState, useCallback, useEffect } from "react";
import { NormalizedPlaybookResult, isPlaybookSuccess } from "../../interfaces/playbookTypes";
import { createLogger } from "../../utils/logger";
import MarkdownRenderer from "../../helpers/MarkdownRenderer";

const log = createLogger("PlaybookViewer");

interface PlaybookViewerProps {
  /** The normalized playbook tool result from the AI */
  result: NormalizedPlaybookResult;
}

/**
 * PlaybookViewer - Minimal inline reference for consulted playbooks
 *
 * Features:
 * - Collapsed: Single line with reason as primary text
 * - Expanded: Document info → Section chips → Scrollable content
 * - Minimal design that doesn't clutter conversation
 */
const PlaybookViewer: React.FC<PlaybookViewerProps> = ({ result }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // Playbooks can be multi-megabyte, and parsing markdown is expensive - so the
  // content must not be mounted while the viewer has never been opened. Once
  // opened it stays mounted, so collapsing and re-expanding doesn't re-parse.
  // Mounting is additionally deferred by one commit (via the effect below):
  // the expand click must paint first (chevron, panel, loading note) or a
  // large playbook's parse would block the main thread while the click still
  // looks dead.
  const [hasEverExpanded, setHasEverExpanded] = useState(false);
  const [contentMounted, setContentMounted] = useState(false);

  useEffect(() => {
    if (hasEverExpanded) {
      setContentMounted(true);
    }
  }, [hasEverExpanded]);

  // Log mount
  useEffect(() => {
    log.debug("PlaybookViewer mounted", {
      reason: result.reason,
      contentLength: result.content_length,
    });
  }, [result]);

  // Handle empty playbook - return null for silent handling.
  // A body that was deliberately stripped is not the same as an empty one: the
  // card still has its title, date and reason, so it keeps rendering and offers
  // to fetch the text again instead of vanishing from the restored message.
  if (!isPlaybookSuccess(result) && !result.playbook_omitted) {
    log.debug("Playbook content is empty or invalid");
    return null;
  }

  const { playbook, reason, playbook_title, last_updated, playbook_omitted } = result;

  // Toggle expansion
  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
    setHasEverExpanded(true);
    log.debug("Playbook expansion toggled", { expanded: !isExpanded });
  }, [isExpanded]);

  // =================================================================
  // STYLES - Translated from Tailwind to inline styles
  // =================================================================

  // Color tokens
  const colors = {
    mutedBg: "rgba(248, 249, 250, 0.5)", // bg-muted/30 equivalent
    mutedBgHover: "rgba(248, 249, 250, 0.8)", // bg-muted/50 equivalent
    mutedBgLight: "rgba(248, 249, 250, 0.3)", // bg-muted/20 equivalent
    border: "rgba(0, 0, 0, 0.08)", // border-border/50 equivalent
    primary: "#0066CC",
    primaryBg: "rgba(0, 102, 204, 0.1)", // bg-primary/10 equivalent
    foreground: "#1a1a1a",
    mutedForeground: "#6c757d",
    separator: "#CED4DA",
  };

  // Container
  const containerStyle: React.CSSProperties = {
    borderRadius: "8px",
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.mutedBg,
    overflow: "hidden",
    width: "100%",
    minWidth: 0,
    marginBottom: "12px",
  };

  // Header button (clickable)
  const headerStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: isHovered ? colors.mutedBgHover : "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left" as const,
    transition: "background-color 0.15s ease",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  };

  // Icon container
  const iconContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    backgroundColor: colors.primaryBg,
    flexShrink: 0,
  };

  // Text content container
  const textContentStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  };

  // Header text line - single row with ellipsis truncation
  const headerTextStyle: React.CSSProperties = {
    fontSize: "13px",
    lineHeight: "1.4",
    color: colors.mutedForeground,
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  };

  // Label span
  const labelStyle: React.CSSProperties = {
    color: colors.foreground,
    fontWeight: 500,
  };

  // Separator dot
  const separatorStyle: React.CSSProperties = {
    color: colors.separator,
    margin: "0 6px",
  };

  // Reason text
  const reasonStyle: React.CSSProperties = {
    fontStyle: "italic",
  };

  // Chevron styles
  const chevronStyle: React.CSSProperties = {
    width: "16px",
    height: "16px",
    color: colors.mutedForeground,
    flexShrink: 0,
    transition: "transform 0.2s ease",
    transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
  };

  // Collapse wrapper — animates max-height for smooth open/close.
  const collapseWrapperStyle: React.CSSProperties = {
    maxHeight: isExpanded ? "500px" : "0",
    overflow: "hidden",
    transition: "max-height 0.3s ease",
  };

  // Expanded content wrapper — border-top only visible when expanded so it
  // doesn't peek out during the collapse animation.
  const expandedContentStyle: React.CSSProperties = {
    borderTop: isExpanded ? `1px solid ${colors.border}` : "none",
  };

  // Document info bar
  const infoBarStyle: React.CSSProperties = {
    padding: "10px 16px",
    backgroundColor: colors.mutedBgLight,
    display: "flex",
    flexWrap: "wrap" as const,
    alignItems: "center",
    gap: "16px",
    fontSize: "12px",
    color: colors.mutedForeground,
  };

  const infoItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };

  // Content area
  const contentAreaStyle: React.CSSProperties = {
    padding: "16px",
    maxHeight: "320px",
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
  };

  // =================================================================
  // ICONS (inline SVG)
  // =================================================================

  const BookIcon = () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={colors.primary}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );

  const FileIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
    </svg>
  );

  const CalendarIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );

  const ChevronIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={chevronStyle}
    >
      <polyline points="9,18 15,12 9,6" />
    </svg>
  );

  // =================================================================
  // RENDER
  // =================================================================

  return (
    <div style={containerStyle}>
      {/* COLLAPSED STATE: Single-line header */}
      <button
        style={headerStyle}
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} playbook reference`}
      >
        {/* Icon container */}
        <div style={iconContainerStyle}>
          <BookIcon />
        </div>

        {/* Text content: "Referenced playbook · reason" */}
        <div style={textContentStyle}>
          <p style={headerTextStyle}>
            <span style={labelStyle}>Retrieved playbook</span>
            {reason && (
              <>
                <span style={separatorStyle}>·</span>
                <span style={reasonStyle}>{reason}</span>
              </>
            )}
          </p>
        </div>

        {/* Expand/collapse chevron */}
        <ChevronIcon />
      </button>

      {/* EXPANDED STATE: Full document viewer */}
      <div
        style={collapseWrapperStyle}
        aria-hidden={!isExpanded}
        // `inert` keeps focus and AT out of the collapsed panel; cast because
        // React's CSSProperties doesn't yet model the HTML attribute.
        {...({ inert: !isExpanded ? "" : undefined } as Record<string, string | undefined>)}
      >
        <div style={expandedContentStyle}>
          {/* Document info bar */}
          <div style={infoBarStyle}>
            {playbook_title && (
              <span style={infoItemStyle}>
                <FileIcon />
                {playbook_title}
              </span>
            )}
            {last_updated && (
              <span style={infoItemStyle}>
                <CalendarIcon />
                Last updated: {last_updated}
              </span>
            )}
          </div>

          {/* Scrollable content area */}
          <div style={contentAreaStyle}>
            {playbook_omitted ? (
              <p
                style={{
                  fontSize: "13px",
                  fontStyle: "italic",
                  color: colors.mutedForeground,
                  margin: 0,
                }}
              >
                The playbook text is not kept with the conversation. Ask a follow-up question and it
                will be retrieved again.
              </p>
            ) : contentMounted ? (
              <MarkdownRenderer>{playbook}</MarkdownRenderer>
            ) : (
              hasEverExpanded && (
                <p
                  style={{
                    fontSize: "13px",
                    fontStyle: "italic",
                    color: colors.mutedForeground,
                    margin: 0,
                  }}
                >
                  Loading playbook preview...
                </p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaybookViewer;
